import React from 'react';
import { Certificate, Medal } from '@phosphor-icons/react';

// Analytics API row shape from learn_my_certificates metric
interface CertificateRow {
    id: string | number;
    courseTitle: string;
    completedAt?: string;
    certificateType?: string;
    certificateUrl?: string;
}

interface CertificatesEarnedWidgetProps {
    data?: CertificateRow[] | null;
    loading?: boolean;
    error?: string;
}

const formatDate = (dateStr?: string): string => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export const CertificatesEarnedWidget: React.FC<CertificatesEarnedWidgetProps> = ({ data, loading, error }) => {
    const certificates = Array.isArray(data) ? data : [];
    const courseCerts = certificates.filter(c => c.certificateType !== 'ilt');
    const iltCerts = certificates.filter(c => c.certificateType === 'ilt');

    return (
        <div className="flex flex-col h-full">
            <div className="px-4 pt-4 pb-4 flex items-center justify-between">
                <span className="text-xs text-gray-400">Lifetime</span>
            </div>
            <div className="flex-1 overflow-auto px-4 pb-4">
                {loading ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="w-6 h-6 border-3 border-gray-200 border-t-primary rounded-full animate-spin" />
                    </div>
                ) : error ? (
                    <div className="flex items-center justify-center h-full text-red-500 text-sm">{error}</div>
                ) : certificates.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                        <Certificate className="w-8 h-8" />
                        <span className="text-sm">No certificates yet</span>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-3 gap-3 mb-4">
                            <div className="text-center p-3 bg-emerald-50 rounded-lg">
                                <div className="text-2xl font-bold text-emerald-600">{certificates.length}</div>
                                <div className="text-[11px] text-gray-500">Total Earned</div>
                            </div>
                            <div className="text-center p-3 bg-blue-50 rounded-lg">
                                <div className="text-2xl font-bold text-blue-600">{courseCerts.length}</div>
                                <div className="text-[11px] text-gray-500">Course Certs</div>
                            </div>
                            <div className="text-center p-3 bg-purple-50 rounded-lg">
                                <div className="text-2xl font-bold text-purple-600">{iltCerts.length}</div>
                                <div className="text-[11px] text-gray-500">ILT Attendance</div>
                            </div>
                        </div>
                        <div className="space-y-2">
                            {certificates.slice(0, 4).map((cert) => (
                                <div key={cert.id} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg">
                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                        cert.certificateType === 'ilt' ? 'bg-purple-100' : 'bg-emerald-100'
                                    }`}>
                                        <Medal className={`w-4 h-4 ${
                                            cert.certificateType === 'ilt' ? 'text-purple-600' : 'text-emerald-600'
                                        }`} weight="fill" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm text-gray-800 truncate">{cert.courseTitle}</div>
                                        <div className="text-[11px] text-gray-400">
                                            {cert.certificateType === 'ilt' ? 'Attended' : 'Completed'} {formatDate(cert.completedAt)}
                                        </div>
                                    </div>
                                    {cert.certificateUrl && (
                                        <span className="text-xs text-primary cursor-pointer hover:underline">View</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
